/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

/**
 * @callback ListDropDownItemRenderer
 * @param {string[]} model splitted string model
 * @param {Object} item the object value
 * @return {string} rendered html
 */

/**
 * @typedef {Object} ListDropDownWidgetItem
 * @property {string} text
 * @property {string} value
 */

modulum("ListDropDownWidget", ["DropDownWidget", "WidgetFactory"], function (context, cls) {
  /**
   * List Drop Down Widget.
   * The values of this widget is meant to be controlled by another widget.
   * @class ListDropDownWidget
   * @memberOf classes
   * @extends classes.DropDownWidget
   */
  cls.ListDropDownWidget = context.oo.Class(cls.DropDownWidget, function ($super) {
    return /** @lends classes.ListDropDownWidget.prototype */ {
      /** @lends classes.ListDropDownWidget */
      $static: {
        /**
         *
         * @private
         */
        _defaultItemRenderer: null,
        /**
         * Produces the default item renderer
         * @todo : See if it's possible to refactor and remove all this special renderer system. And just compose a widget
         * @return {ListDropDownItemRenderer}
         */
        getDefaultItemRenderer: function () {
          if (!cls.ListDropDownWidget._defaultItemRenderer) {
            const itemElement = context.TemplateService.renderDOM("ListDropDownItem");
            itemElement.setAttribute("combovalue", "#$value#");
            // full text as native tooltip: the visible text is ellipsized when it overflows the item
            itemElement.setAttribute("title", "#$text#");
            const labelElement = itemElement.getElementsByClassName("gbc_ListDropDownWidget__itemText")[0];
            labelElement.textContent = "#$text#";
            const model = itemElement.outerHTML.split("#");
            cls.ListDropDownWidget._defaultItemRenderer = function (model, item) {
              let result = "";
              for (const element of model) {
                // escape HTML conflicting chars such as quote and double-quotes before adding it in the DOM
                result += element[0] === "$" ? cls.StringHelper.escapeHTML(item[element.slice(1)]) || "" : element;
              }
              return result;
            }.bind(null, model);
          }
          return cls.ListDropDownWidget._defaultItemRenderer;
        }
      },

      __name: "ListDropDownWidget",
      __templateName: "DropDownWidget",
      /**
       * @inheritDoc
       */
      autoSize: true,
      /**
       * whether or not this list will allow multiple value selection
       * @type {boolean}
       */
      _allowMultipleValues: false,
      /**
       * whether or not this list will allow null value
       * @type {boolean}
       */
      _notNull: false,
      /**
       * current items - raw data
       * @type {Object[]}
       */
      _items: null,

      /**
       * currently highlighted element
       * @type {number}
       */
      _currentIndex: -1,

      /** Last selected item index */
      _lastSelectedValueIndex: -1,
      /**
       * currently set value
       * @type {string[]}
       */
      _selectedValues: null,

      /**
       * item renderer
       * @type {ListDropDownItemRenderer}
       */
      _itemRenderer: null,

      /**
       * @inheritdoc
       */
      constructor: function (opts) {
        this._selectedValues = [];
        $super.constructor.call(this, opts);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._removeHoverBindings();
        this._items.length = 0;
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      _initContainerElement: function () {
        $super._initContainerElement.call(this);

        // ListDropDown is a true WAI-ARIA listbox; the base DropDown template no longer
        // sets a default role, every consumer is expected to declare its own.
        this.setAriaRole("listbox");

        this._items = [];
        this._itemRenderer = cls.ListDropDownWidget.getDefaultItemRenderer();

        this.onOpen(this._addHoverBindings.bind(this));
        this.onClose(this._removeHoverBindings.bind(this));

        this._updateVisualElementState();
      },

      /**
       * Bind dropdown items hover styling events
       * @private
       */
      _addHoverBindings: function () {
        if (!cls.WindowHelper.isMobile()) {
          cls.DOMElementHelper.on(this._containerElement, "mouseover.ListDropDownWidget", this._onMouseover.bind(this));
        }
      },

      /**
       * Remove dropdown items hover styling events
       * @private
       */
      _removeHoverBindings: function () {
        if (!cls.WindowHelper.isMobile()) {
          cls.DOMElementHelper.off(this._containerElement, "mouseover.ListDropDownWidget");
        }
      },

      /**
       * Set the available items
       * @param {ListDropDownWidgetItem[]} list items
       */
      setItems: function (list) {
        this._items = [];
        list.forEach((element) => {
          this.getItems().push({
            text: element.text,
            value: element.value,
            searchText: element.text.toLocaleLowerCase(),
            nullItem: element.nullItem,
            virtualItem: false,
            searchPattern: ""
          });
        });

        this.updateUIList();
      },

      /**
       * Get the items
       * @return {ListDropDownWidgetItem[]} list items
       */
      getItems: function () {
        return this._items;
      },

      /**
       * Return the items elements
       * @returns {HTMLCollectionOf<HTMLElement>} live collection: snapshot it before any DOM-mutating iteration
       */
      getChildren: function () {
        return this._containerElement.getElementsByClassName(this.getItemClassName());
      },

      /**
       * Rebuild the dropdown's DOM list from `this._items`.
       * @todo Optimise: this fully rebuilds on every call (re-filters virtual items, recomputes each item's
       *   searchPattern, and regenerates the whole innerHTML) even when nothing changed. Rebuild only on
       *   actual item changes, and move the null-item detection into `setItems()` so it is not recomputed
       *   on every refresh.
       */
      updateUIList: function () {
        let hasNull = false,
          hasNotNull = false;

        const content = [];

        const isConstruct = this.getParentWidget().getDialogType() === "Construct",
          simpleConstruct = isConstruct && this.getParentWidget().useSimpleConstructNullItem?.() === true,
          isNullText = gbc.I18NService.t("gwc.main.combobox.isnull");

        this._items = this._items.filter(function (i) {
          return !i.virtualItem;
        });

        for (const item of this.getItems()) {
          hasNull ||= isConstruct && item.value === "=";
          hasNotNull ||= isConstruct && item.value.match(/!=|<>/);

          if (!hasNull && item.nullItem) {
            if (simpleConstruct) {
              // GDC native: the null item becomes the single empty item that clears the field
              item.value = "";
              item.text = "";
            } else {
              item.value = isConstruct ? "=" : "";
              item.text = isConstruct && item.text === "" ? isNullText : item.text;
            }
            item.searchText = cls.StringHelper.escapeHTML(item.text.toLocaleLowerCase());
            hasNull = true;
          }

          item.searchPattern = this._buildItemRegex(item.value, item.text);

          content.push(this._itemRenderer(item));
        }

        this._appendConstructVirtualItems(content, isConstruct, simpleConstruct, hasNull, hasNotNull, isNullText);

        this._containerElement.innerHTML = content.join("");

        if (this.isVisible()) {
          this.hide();
          this.show();
        }

        this._updateAriaAttributes();
      },

      /**
       * Append the CONSTRUCT-only virtual items to this._items and their rendered HTML to `content`.
       * - simpleConstruct (GDC native): exactly one trailing empty item that clears the field on
       *   selection, and NO <blank>(=) / <not blank>(!=) items, whatever the field nullability.
       * - otherwise (default combobox, nullable field): a <blank>(=) item and a <not blank>(!=) item.
       * @param {string[]} content rendered item HTML fragments (mutated in place)
       * @param {boolean} isConstruct whether the dialog type is CONSTRUCT
       * @param {boolean} simpleConstruct whether the parent uses the GDC-native simple-construct null item
       * @param {boolean} hasNull whether a null/blank item is already present in the list
       * @param {boolean} hasNotNull whether a not-blank item is already present in the list
       * @param {string} isNullText localized <blank> label
       * @private
       */
      _appendConstructVirtualItems: function (content, isConstruct, simpleConstruct, hasNull, hasNotNull, isNullText) {
        if (simpleConstruct) {
          if (!hasNull) {
            this._items.push({
              text: "",
              value: "",
              searchText: "",
              nullItem: true,
              virtualItem: true,
              searchPattern: this._buildItemRegex("", "")
            });
            content.push(this._itemRenderer(this._items.at(-1)));
            this._layoutEngine.invalidateMeasure();
            this._layoutEngine.needMeasure();
          }
          return;
        }

        if (this._notNull) {
          return;
        }

        const isNotNullText = gbc.I18NService.t("gwc.main.combobox.isnotnull");
        let needMeasure = false;

        if (!hasNull) {
          this._items.push({
            text: isConstruct ? cls.StringHelper.escapeHTML(isNullText) : "",
            value: isConstruct ? "=" : "",
            searchText: isConstruct ? isNullText.replaceAll(/[<>＜＞]/gu, "") : "",
            nullItem: true,
            virtualItem: true,
            searchPattern: this._buildItemRegex(isConstruct ? "=" : "", isConstruct ? cls.StringHelper.escapeHTML(isNullText) : "")
          });
          content.push(this._itemRenderer(this._items.at(-1)));
          needMeasure = true;
        }

        if (!hasNotNull && isConstruct) {
          this._items.push({
            text: cls.StringHelper.escapeHTML(isNotNullText),
            value: "!=",
            searchText: isNotNullText.replaceAll(/[<>＜＞]/gu, ""),
            nullItem: false,
            virtualItem: true,
            searchPattern: this._buildItemRegex("!=", cls.StringHelper.escapeHTML(isNotNullText))
          });
          content.push(this._itemRenderer(this._items.at(-1)));
          needMeasure = true;
        }

        if (needMeasure) {
          this._layoutEngine.invalidateMeasure();
          this._layoutEngine.needMeasure();
        }
      },

      /**
       * update items accessibility attributes
       * @private
       */
      _updateAriaAttributes: function () {
        this.domAttributesMutator(
          function () {
            // snapshot: keep iteration O(n) even if class writes get added to this loop someday
            const items = Array.from(this.getChildren()),
              len = items.length;
            // role="option" is set statically in ListDropDownItem.tpl.html
            for (let i = 0; i < len; ++i) {
              // stable id so the combobox can target this option via aria-activedescendant
              items[i].id = this._optionId(i);
              items[i].setAttribute("aria-posinset", (i + 1).toString());
              items[i].setAttribute("aria-setsize", len.toString());
            }
          }.bind(this)
        );
      },

      /**
       * Check items checkbox accordingly to selectedValues
       * Set current item accordingly to _currentItem
       * @private
       */
      _updateVisualElementState: function () {
        // snapshot: class writes invalidate the live collection cache, so iterating it directly re-scans the DOM at each step (quadratic)
        const children = Array.from(this.getChildren());
        let index = 0;
        for (const child of children) {
          const isSelected = this._selectedValues.includes(child.getAttribute("combovalue"));
          cls.DOMElementHelper.toggleClasses(child, isSelected, context.constants.widgetCssClasses.isSelected);
          cls.DOMElementHelper.toggleClass(child, context.constants.widgetCssClasses.current, index === this._currentIndex);
          // role="option" expects aria-selected to reflect the item's selection state
          cls.DOMElementHelper.updateAttribute(child, "aria-selected", String(isSelected));
          index += 1;
        }
      },

      /**
       * Get item index by its value
       * @param {string} value value
       * @return {number} The matching index. -1 if not found
       */
      indexByValue: function (value) {
        return this._items.findIndex((item) => item.value === value);
      },

      /**
       * Get item by its value
       * @param {string} value value
       * @return {Object} the item
       */
      findByValue: function (value) {
        return this._items.find(function (item) {
          return item.value === value;
        });
      },
      /**
       * Get item by its text
       * @param {string} text value
       * @param {boolean} caseSensitive true by default
       * @return {Object} the item
       */
      findByText: function (text, caseSensitive) {
        if (!caseSensitive) {
          text = text.toLocaleLowerCase();
        }

        return this._items.find((item) => item.text === text);
      },

      /**
       * find item starting by searchPattern beginning at current index. will loop to cover all items.
       * @param {string} searchPattern first letters of searched item
       * @param {boolean} startAfterCurrentItem exclude current index item as first matching element
       * @return {Object} found item
       */
      findStartingByText: function (searchPattern, startAfterCurrentItem) {
        const current = this._currentIndex + (startAfterCurrentItem ? 1 : 0);

        return (
          this._items.find(function (item, i) {
            return i >= current && item.searchText.match(new RegExp(`^${searchPattern}`, ""));
          }) ||
          this._items.find(function (item, i) {
            return i < current && item.searchText.match(new RegExp(`^${searchPattern}`, ""));
          })
        );
      },

      /**
       * get value of the current index
       * @return {string|null} Return the value of the current item, if no current item (current index == -1) return null
       */
      getCurrentValue: function () {
        if (this._currentIndex < 0 || this._currentIndex >= this.getItems().length) {
          return null;
        }
        return this.getItems()[this._currentIndex].value;
      },

      /**
       * @returns The index of the current item
       */
      getCurrentIndex: function () {
        return this._currentIndex;
      },

      /**
       * Stable DOM id of the option at given index. Scoped by the dropdown root class
       * (uuid-based), so ids stay unique across comboboxes and stable per rendered list.
       * @param {number} index
       * @returns {string} the option element id
       * @private
       */
      _optionId: function (index) {
        return `${this.getRootClassName()}_opt_${index}`;
      },

      /**
       * Id of the currently highlighted option, for the combobox aria-activedescendant.
       * @returns {?string} active option id, or null when nothing is active / list is closed
       */
      getActiveDescendantId: function () {
        if (!this.isVisible() || this._currentIndex < 0 || this._currentIndex >= this.getItems().length) {
          return null;
        }
        return this._optionId(this._currentIndex);
      },

      /**
       * Set the selected values of the widget.
       * And update the UI
       * @param {string[]} values The selected values
       */
      setSelectedValues: function (values) {
        if (!Array.isArray(values)) {
          return;
        }
        this._selectedValues = values;
        const index = this.indexByValue(this._selectedValues.at(-1));
        this._lastSelectedValueIndex = index;
        // Get the last value and set it as current
        this._updateVisualElementState();
      },

      /**
       * Set current index and potentially scroll to it
       * !! WARNING !! : This method don't change any "selected Value"
       * @param {number} index The new index, this index will be clamped between 0 and items count
       */
      setCurrentIndex: function (index) {
        index = cls.MathHelper.clamp(index, -1, this.getItems().length - 1);

        if (this._currentIndex !== -1) {
          const previousItem = this.getChildren()[this._currentIndex];
          previousItem?.classList.remove(context.constants.widgetCssClasses.current);
        }

        this._currentIndex = index;

        const currentItem = this.getChildren()[this._currentIndex];
        currentItem?.classList.add(context.constants.widgetCssClasses.current);
        if (currentItem && this.isVisible()) {
          this.scrollItemIntoView(currentItem);
        }

        // notify the owner (e.g. ComboBox) so it can move aria-activedescendant on the
        // element carrying role=combobox (the input) — DOM focus never leaves the input.
        this.getParentWidget()?.onListDropDownCurrentChanged?.();
      },

      setCurrentIndexOnLastItem: function () {
        // Set the current index as the last value of the selectedValues
        const index = this.indexByValue(this._selectedValues.at(-1));
        this.setCurrentIndex(index);
      },

      /**
       * Select the item. And update the UI
       * @param {string} value value
       * @private
       */
      _selectItem: function (value) {
        if (value === null) {
          return;
        }

        const itemIndex = this.getItems().findIndex((item) => item.value === value);
        if (itemIndex === -1) {
          return;
        }

        this._lastSelectedValueIndex = itemIndex;
        this._updateVisualElementState();

        this.emit(context.constants.widgetEvents.select, value, this.getItems()[itemIndex].text);
      },

      /**
       * sorts input array in the same order as items array
       * @param {string[]} values input values
       * @return {string[]} sorted array
       */
      sortValues: function (values) {
        const result = [],
          isConstruct = this.getParentWidget().getDialogType() === "Construct";
        let hasNull = false;

        for (const item of this._items) {
          if (values.indexOf(item.value) >= 0) {
            result.push(item.value);
            //On construct "=" equal "NULL" item because it means 'is not null'
            if (item.nullItem || (item.value === "=" && isConstruct)) {
              hasNull = true;
            }
          }
        }

        const nullKey = isConstruct ? "=" : "";
        if (!hasNull && values.indexOf(nullKey) >= 0) {
          result.push(nullKey);
        }

        return result;
      },

      /**
       * Apply a CSS property to every dropdown item (scoped to the item class).
       * @param {string} cssProp - CSS property name
       * @param {*} value - CSS value
       * @private
       */
      _setItemStyle: function (cssProp, value) {
        this.setStyle(
          {
            selector: `.${this.getItemClassName()}`
          },
          {
            [cssProp]: value
          }
        );
      },

      /**
       * @inheritDoc
       */
      setFontFamily: function (fontFamily) {
        const escaped = fontFamily === null || fontFamily === undefined ? fontFamily : cls.StringHelper.escapeFontFamily(fontFamily);
        this._setItemStyle("font-family", escaped);
      },

      /**
       * @inheritDoc
       */
      setFontWeight: function (weight) {
        this._setItemStyle("font-weight", weight);
      },

      /**
       * @inheritDoc
       */
      setFontStyle: function (style) {
        this._setItemStyle("font-style", style);
      },

      /**
       * @inheritDoc
       */
      setTextAlign: function (align) {
        this._setItemStyle("text-align", align);
      },

      /**
       * set whether or not this list will allow null value
       * @param {boolean} notNull not null
       */
      setNotNull: function (notNull) {
        if (this._notNull !== notNull) {
          this._notNull = notNull;
          this.updateUIList();
        }
      },

      /**
       * set whether or not this list allow multiple choices
       * @param {boolean} allow allow
       */
      allowMultipleChoices: function (allow) {
        this._allowMultipleValues = allow;
        this.toggleClass("gbc_singleChoice", !allow);
        // listbox parent must advertise aria-multiselectable so screen readers know
        // selecting one option does not deselect the others
        this.setAriaAttribute("multiselectable", allow ? "true" : null);
        this._updateVisualElementState();
      },

      /**
       * Set the listbox accessible name (mirrors the owning combobox name). GBC fields have
       * no <label> element to reference, so the name is applied as aria-label.
       * @param {?string} name - accessible name, or null/empty to remove it
       */
      setAccessibleName: function (name) {
        this.setAriaAttribute("label", name || null);
      },

      /**
       * set current index on item
       * @param {Object} item item
       */
      navigateToItem: function (item) {
        this.setCurrentIndex(this.indexByValue(item.value));
      },

      /**
       * Scroll to element if needed
       * @param {HTMLElement} element element
       * @publicdoc
       */
      scrollItemIntoView: function (element) {
        if (this.getElement().scrollTop > element.offsetTop) {
          element.scrollIntoView();
        } else if (this.getElement().scrollTop + this.getElement().offsetHeight < element.offsetTop + element.offsetHeight) {
          element.scrollIntoView(false);
        }
      },

      /**
       * On click handler raised when selecting an item in the dropdown :
       * Parent widget get value of clicked item and dropdown is closed.
       * @param domEvent
       * @private
       */
      _onClick: function (domEvent) {
        const item = domEvent.target.closest(`.${this.getItemClassName()}`),
          value = item?.getAttribute("combovalue");
        if (value !== null) {
          this._selectItem(value);
          if (domEvent) {
            domEvent.stopPropagation();
          }
          if (!this._allowMultipleValues) {
            this.hide();
          }
        }
      },

      /**
       * Mouse over handler used to highlight current item.
       * @param event
       * @private
       */
      _onMouseover: function (event) {
        const element = event.target;
        if (!element) {
          return;
        }
        const widgetElement = element.closest(`.${this.getItemClassName()}`);
        if (widgetElement) {
          this.setCurrentIndex(cls.DOMElementHelper.index(widgetElement));
        }
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        this._onClick(domEvent);
        return false;
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        let keyProcessed = false;

        switch (keyString) {
          case "space":
            if (this.isVisible()) {
              if (this._allowMultipleValues) {
                this._selectItem(this.getCurrentValue());
              } else {
                this.hide();
              }
              keyProcessed = true;
            }
            break;
          case "enter":
          case "return":
            if (this.isVisible()) {
              if (!this._allowMultipleValues) {
                this._selectItem(this.getCurrentValue());
              }
              this.hide();
              keyProcessed = true;
            }
            break;
          case "up":
            this._manageValueChangeKey(this._currentIndex - 1);
            keyProcessed = true;
            break;
          case "down":
            this._manageValueChangeKey(this._currentIndex + 1);
            keyProcessed = true;
            break;
          case "pageup":
            this._manageValueChangeKey(this._currentIndex - 10);
            keyProcessed = true;
            break;
          case "pagedown":
            this._manageValueChangeKey(this._currentIndex + 10);
            keyProcessed = true;
            break;
          case "home":
            if (this.isVisible()) {
              this._manageValueChangeKey(0);
              keyProcessed = true;
            }
            break;
          case "end":
            if (this.isVisible()) {
              this._manageValueChangeKey(this.getItems().length - 1);
              keyProcessed = true;
            }
            break;
          case "tab":
          case "shift+tab":
            if (this.isVisible()) {
              this.hide();
            }
            break;
        }

        if (keyProcessed) {
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Macro for managing the up, down, pageup, pagedown, start and end key
       * @param {number} index
       * @private
       */
      _manageValueChangeKey(index) {
        if (index < 0 && this.getCurrentIndex() === -1) {
          index = this.getItems().length + (index + 1);
        }
        index = cls.MathHelper.clamp(index, 0, this.getItems().length - 1);
        this.setCurrentIndex(index);
      },

      /**
       * @inheritDoc
       */
      addChildWidget: function (widget, options) {
        // nothing intentionally here
      },

      /**
       * @inheritDoc
       */
      removeChildWidget: function (widget) {
        // nothing intentionally here
      },

      /**
       * Show the widget dropdown
       */
      show: function () {
        $super.show.call(this);

        // Set the current item as the current value (or the last if multiple values are allowed)
        this.setCurrentIndex(this._lastSelectedValueIndex);
      },

      hide: function () {
        $super.hide.call(this);

        this.setCurrentIndex(this._lastSelectedValueIndex);
      },

      /**
       * Create a regex that help finding possible values
       * @param {string} valueToMatch The value/key of the item
       * @param {string} textToMatch The label/text of the item
       * @returns {string} The string to build a regex
       * @private
       */
      _buildItemRegex: function (valueToMatch, textToMatch) {
        if (valueToMatch === undefined && textToMatch === undefined) {
          return "";
        }
        valueToMatch = valueToMatch.replaceAll(/[/\-\\^$*+?.()|[\]{}]/g, "\\$&");
        textToMatch = textToMatch.replaceAll(/[/\-\\^$*+?.()|[\]{}]/g, "\\$&");
        if (textToMatch !== "") {
          valueToMatch = `(?:${valueToMatch})|(?:${textToMatch})`;
        }
        return `(^|\\|)(?:${valueToMatch})(\\||$)`;
      },

      /**
       * Build a match list keyed by the matched substring. For every item, its searchPattern is
       * tested against the value string case-sensitively first (recorded as the exact match), then
       * case-insensitively (recorded as a candidate). filterValues resolves each key to one item.
       * @param {string} valueString The value string to build the match list against
       * @returns {Object} The match list used to build the filtered values
       */
      _buildMatchList: function (valueString) {
        const matches = {};
        for (const item of this.getItems()) {
          let exactMatch = false;

          let matchingValue = valueString.match(new RegExp(item.searchPattern, "g"));
          if (matchingValue) {
            exactMatch = true;
          } else {
            matchingValue = valueString.match(new RegExp(item.searchPattern, "gi"));
          }
          if (matchingValue) {
            if (!matches[matchingValue]) {
              matches[matchingValue] = {
                exactMatch: null,
                matches: []
              };
            }
            if (exactMatch) {
              matches[matchingValue].exactMatch = item;
            } else {
              matches[matchingValue].matches.push(item);
            }
          }
        }
        return matches;
      },

      /**
       * Test if a value exist in the possible values.
       * The argument value is tested against item key or text.
       * @param {string} valueString The value to test agains
       * @param {string} discardUnavailableValues Should this method return empty string if the valueString is not in the availble items. False by defualt
       * @returns {{remaining : string, values : string[], replacedValue: string, replacedText: string}} The value keys that where found in the value given in parameter.
       */
      filterValues: function (valueString, discardUnavailableValues = false) {
        if (!valueString) {
          // Check if the value exists in the items
          const item = this.getItems().find((item) => item.value === valueString);

          return {
            remaining: "",
            values: [""],
            formattedValues: "",
            formattedText: item?.text !== undefined ? item.text : ""
          };
        }
        let remaining = valueString;
        let formattedValues = valueString;
        let formattedText = valueString;

        const matchedItems = this._buildMatchList(valueString);
        let processedValues = [];
        // filter again, so if there are multiple matches
        // and no exact match, we consider that no values are valid
        for (const valueMatch in matchedItems) {
          let matchedItem = null;
          let flags = "g";
          if (matchedItems[valueMatch].exactMatch) {
            matchedItem = matchedItems[valueMatch].exactMatch;
          } else if (matchedItems[valueMatch].matches.length === 1) {
            matchedItem = matchedItems[valueMatch].matches[0];
            flags += "i";
          }
          if (matchedItem) {
            processedValues.push(matchedItem.value);
            const reg = new RegExp(matchedItem.searchPattern, flags);
            remaining = remaining.replaceAll(reg, "");
            formattedValues = formattedValues.replaceAll(reg, `$1${matchedItem.value}$2`);
            formattedText = formattedText.replaceAll(reg, `$1${matchedItem.text}$2`);
          }
        }

        // Remove the pipes that could have stayed
        remaining = remaining.replace("|", "");
        /* If we do not allow values that are not in the items,
         * and no values was found, that mean we should not return anything
         */
        if (discardUnavailableValues && processedValues.length === 0) {
          formattedValues = "";
          formattedText = "";
        }

        return {
          remaining,
          values: processedValues,
          formattedValues,
          formattedText
        };
      },

      /**
       * Get the item CSS class name
       */
      getItemClassName: function () {
        return `gbc_${this.__name}__item`;
      }
    };
  });
  cls.WidgetFactory.registerBuilder("ListDropDown", cls.ListDropDownWidget);
});
